import React, { useState, useEffect } from 'react';
import { productAPI } from '../services/productApi';
import { categoryAPI } from '../services/categoryApi';
import { Product, Category, ProductStatus } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Search, AlertCircle, CheckCircle, X, Package } from 'lucide-react';

const ProductsPage: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);

    const { selectedCompany } = useCompany();
    const { t, isRTL } = useI18n();

    useEffect(() => {
        if (selectedCompany.selectedCompanyId) {
            loadData();
        } else {
            setProducts([]);
            setCategories([]);
            setLoading(false);
        }
    }, [selectedCompany.selectedCompanyId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [productsData, categoriesData] = await Promise.all([
                productAPI.getProductsByCompany(selectedCompany.selectedCompanyId!),
                categoryAPI.getCategoriesByCompany(selectedCompany.selectedCompanyId!)
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
            setError(null);
        } catch (err) {
            setError(t('stock.products.failedToLoad'));
            console.error('Error loading products:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdate = async (productData: Partial<Product>) => {
        try {
            if (editingProduct) {
                const updated = await productAPI.updateProduct(editingProduct.id, productData);
                setProducts(products.map(p => p.id === editingProduct.id ? updated : p));
                setSuccessMessage(t('stock.products.productUpdated'));
            } else {
                const created = await productAPI.createProduct({
                    ...productData,
                    companyId: selectedCompany.selectedCompanyId!,
                    active: true,
                    quantityOnHand: productData.quantityOnHand || 0,
                    reservedQuantity: 0,
                    status: 'ACTIVE'
                });
                setProducts([...products, created]);
                setSuccessMessage(t('stock.products.productCreated'));
            }
            setShowModal(false);
            setEditingProduct(null);
        } catch (err) {
            setError(t('stock.products.failedToUpdate'));
            console.error('Error saving product:', err);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm(t('stock.products.confirmDelete'))) {
            try {
                await productAPI.deleteProduct(id);
                setProducts(products.filter(p => p.id !== id));
                setSuccessMessage(t('stock.products.productDeleted'));
            } catch (err) {
                setError(t('stock.products.failedToDelete'));
                console.error('Error deleting product:', err);
            }
        }
    };

    const getCategoryName = (categoryId?: string) => {
        if (!categoryId) return '-';
        return categories.find(c => c.id === categoryId)?.name || '-';
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!selectedCompany.selectedCompanyId) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-64">
                    <div className="text-center">
                        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
                        <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{t('stock.products.title')}</h1>
                    <p className="text-gray-600 mt-1">
                        {t('stock.products.subtitle', { companyName: selectedCompany.selectedCompany?.name })}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            setEditingProduct(null);
                            setShowModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <Plus size={20} />
                        {t('stock.products.createNew')}
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <CheckCircle size={18} />
                        <span>{successMessage}</span>
                    </div>
                    <button onClick={() => setSuccessMessage(null)}><X size={18} /></button>
                </div>
            )}

            <div className="mb-6">
                <div className="relative">
                    <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                </div>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.name')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.code')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.category')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.unitPrice')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('invoices.unit')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.purchasePrice')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.quantityOnHand')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.availableQuantity')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.status')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.products.actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredProducts.map((product) => (
                                <tr key={product.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {product.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.code}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {getCategoryName(product.categoryId)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                        {product.unitPrice.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.unity || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.purchasePrice?.toFixed(2) || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {product.quantityOnHand}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                                        {product.quantityOnHand - product.reservedQuantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${product.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                            product.status === 'OUT_OF_STOCK' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                            {product.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => {
                                                    setEditingProduct(product);
                                                    setShowModal(true);
                                                }}
                                                className="text-green-600 hover:text-green-900"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(product.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredProducts.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                        {t('stock.products.noProductsFound')}
                    </div>
                )}
            </div>

            {showModal && (
                <ProductModal
                    product={editingProduct}
                    categories={categories}
                    onClose={() => setShowModal(false)}
                    onSubmit={handleCreateOrUpdate}
                />
            )}
        </Layout>
    );
};

interface ProductModalProps {
    product: Product | null;
    categories: Category[];
    onClose: () => void;
    onSubmit: (data: Partial<Product>) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, categories, onClose, onSubmit }) => {
    const { t, isRTL } = useI18n();
    const [formData, setFormData] = useState<Partial<Product>>({
        name: product?.name || '',
        code: product?.code || '',
        categoryId: product?.categoryId || '',
        unitPrice: product?.unitPrice || 0,
        unity: product?.unity || '',
        purchasePrice: product?.purchasePrice || 0,
        quantityOnHand: product?.quantityOnHand || 0,
        status: product?.status || 'ACTIVE'
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Package className="text-blue-600" />
                    {product ? t('stock.products.editProduct') : t('stock.products.createNew')}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.name')} *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.code')} *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.category')}
                            </label>
                            <select
                                value={formData.categoryId}
                                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t('common.select')}</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.unitPrice')} *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                required
                                value={formData.unitPrice}
                                onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('invoices.unit')}
                            </label>
                            <input
                                type="text"
                                value={formData.unity}
                                onChange={(e) => setFormData({ ...formData, unity: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. kg, m, u"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.purchasePrice')}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.purchasePrice}
                                onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.quantityOnHand')}
                            </label>
                            <input
                                type="number"
                                value={formData.quantityOnHand}
                                onChange={(e) => setFormData({ ...formData, quantityOnHand: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                disabled={!!product} // Disable initial quantity on edit (should use movements)
                            />
                            {product && <p className="text-xs text-gray-500 mt-1">{t('stock.products.quantityOnEditWarning')}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('stock.products.status')}
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value as ProductStatus })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                                <option value="OUT_OF_STOCK">OUT OF STOCK</option>
                                <option value="DISCONTINUED">DISCONTINUED</option>
                            </select>
                        </div>
                    </div>
                    <div className={`flex justify-end gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            {product ? t('common.save') : t('common.create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductsPage;
